import { useState } from "react";
import { useParams } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { PDFViewer } from "@/components/viewers/PDFViewer";
import { SummaryPanel } from "@/components/viewers/SummaryPanel";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useSummariesManifest, type SummarySection } from "@/hooks/useSummariesManifest";
import { useViewerTracker } from "@/hooks/useViewerTracker";

export function TextbookPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Smart Textbook");
  const { reportEndReached } = useViewerTracker({
    chapterId,
    mode: "textbook",
    active: paywall === null,
    minDwellSeconds: 180,
  });

  // Completion = last page reached AND the dwell floor met. Flicking the
  // scrollbar to the bottom is not finishing a chapter.
  const handlePageChange = (page: number, totalPages: number) => {
    if (totalPages > 0 && page >= totalPages) reportEndReached();
  };

  const { url, isLoading, isError, isLocked, refresh } = useSignedUrl({
    chapterId,
    assetType: "textbook",
    enabled: shouldFetch,
  });

  const summaries = useSummariesManifest(chapterId, shouldFetch);
  const [openSection, setOpenSection] = useState<SummarySection | null>(null);

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  const sectionsForViewer =
    summaries.sections.length > 0 ? summaries.sections : undefined;

  const handleSectionClick = (section: { id: string; title: string }) => {
    const full = summaries.sections.find((s) => s.id === section.id);
    setOpenSection(full ?? { id: section.id, title: section.title });
  };

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Smart Textbook"
      modeIcon={BookOpen}
      modeAccent="bg-blue-500/10 text-blue-600 dark:text-blue-400"
      showHeader={false}
    >
      {paywall ?? (
        isLocked ? (
          <PaywallCard modeLabel="Smart textbook" classId={classId} />
        ) : isError ? (
          <GenericError onRetry={refresh} />
        ) : url ? (
          <PDFViewer
            url={url}
            title="Smart Textbook"
            subtitle={chapter?.chapter_name ?? null}
            helperText={sectionsForViewer ? "Tap a section in the sidebar to read its summary." : null}
            modeIcon={BookOpen}
            modeAccent="bg-blue-500/10 text-blue-600 dark:text-blue-400"
            showStudyControls
            onPageChange={handlePageChange}
            className="h-[100dvh] min-h-0"
            sections={sectionsForViewer}
            onSectionClick={handleSectionClick}
          />
        ) : isLoading ? (
          <PDFLoadingPlaceholder />
        ) : null
      )}

      <SummaryPanel
        open={openSection !== null}
        section={openSection}
        onClose={() => setOpenSection(null)}
      />
    </ViewerScaffold>
  );
}

function PDFLoadingPlaceholder() {
  return (
    <div className="flex-1 grid place-items-center text-sm text-text-muted">
      Preparing your textbook…
    </div>
  );
}
